import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, BookOpen, ClipboardList, Plus, Trash2, Copy, UserPlus, ChevronRight, LogOut, User, Lock, FileText, Search, Check, Download, Share2, Eye, Sparkles } from 'lucide-react';
import MathRenderer from '../components/MathRenderer';
import TestGenerator from '../components/TestGenerator';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function TeacherPortal() {
  const { user, API, getHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', class_level: 6, section: '' });
  const [studentCode, setStudentCode] = useState('');
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ title: '', description: '', class_id: '', due_date: '' });
  // Profile state
  const [profile, setProfile] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', school_name: '' });
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '' });
  // Question Bank state
  const [qbFilters, setQbFilters] = useState({ class_level: 6, chapter: '', difficulty: '', question_type: '' });
  const [qbQuestions, setQbQuestions] = useState([]);
  const [qbTotal, setQbTotal] = useState(0);
  const [qbPage, setQbPage] = useState(1);
  const [selectedQIds, setSelectedQIds] = useState([]);
  const [config, setConfig] = useState(null);
  // Worksheet state
  const [worksheets, setWorksheets] = useState([]);
  const [wsTitle, setWsTitle] = useState('');
  const [viewWorksheet, setViewWorksheet] = useState(null);
  const [viewWsQuestions, setViewWsQuestions] = useState([]);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  // Batch grading
  const [grading, setGrading] = useState(false);
  const [gradeResults, setGradeResults] = useState(null);

  useEffect(() => { fetch(`${API}/config/all`).then(r => r.json()).then(setConfig).catch(() => {}); }, [API]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/teacher/dashboard`, { headers: getHeaders() });
      if (!res.ok) throw new Error();
      setDashboard(await res.json());
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchProfile = async () => {
    const res = await fetch(`${API}/teacher/profile`, { headers: getHeaders() });
    const d = await res.json();
    setProfile(d);
    setProfileForm({ name: d.user?.name || '', school_name: d.profile?.school_name || '' });
  };

  const handleLogout = async () => { await logout(); navigate('/', { replace: true }); };

  const createClass = async () => {
    try {
      await fetch(`${API}/teacher/class/create`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(newClass) });
      toast.success('Class created!');
      setShowCreateClass(false); setNewClass({ name: '', class_level: 6, section: '' }); fetchDashboard();
    } catch { toast.error('Failed'); }
  };

  const fetchStudents = async (classId) => {
    const res = await fetch(`${API}/teacher/class/${classId}/students`, { headers: getHeaders() });
    setClassStudents((await res.json()).students || []);
  };

  const addStudent = async () => {
    if (!studentCode || !selectedClass) return;
    try {
      const res = await fetch(`${API}/teacher/class/${selectedClass.id}/add-student`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ student_code: studentCode }) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success('Student added!'); setStudentCode(''); fetchStudents(selectedClass.id);
    } catch (e) { toast.error(e.message); }
  };

  const deleteClass = async (classId) => {
    if (!window.confirm('Delete?')) return;
    await fetch(`${API}/teacher/class/${classId}`, { method: 'DELETE', headers: getHeaders() });
    setSelectedClass(null); fetchDashboard();
  };

  const createAssignment = async () => {
    try {
      await fetch(`${API}/teacher/assignment/create`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ ...newAssignment, question_ids: selectedQIds }) });
      toast.success('Assignment created!');
      setShowCreateAssignment(false); fetchDashboard();
    } catch { toast.error('Failed'); }
  };

  const fetchQuestionBank = async () => {
    const params = new URLSearchParams({ page: qbPage, limit: 15 });
    if (qbFilters.class_level) params.set('class_level', qbFilters.class_level);
    if (qbFilters.chapter) params.set('chapter', qbFilters.chapter);
    if (qbFilters.difficulty) params.set('difficulty', qbFilters.difficulty);
    if (qbFilters.question_type) params.set('question_type', qbFilters.question_type);
    const res = await fetch(`${API}/teacher/question-bank?${params}`, { headers: getHeaders() });
    const d = await res.json();
    setQbQuestions(d.questions || []); setQbTotal(d.total || 0);
  };

  const fetchWorksheets = async () => {
    const res = await fetch(`${API}/teacher/worksheets`, { headers: getHeaders() });
    setWorksheets((await res.json()).worksheets || []);
  };

  const createWorksheet = async () => {
    if (!selectedQIds.length) { toast.error('Select questions first'); return; }
    await fetch(`${API}/teacher/worksheet/create`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ title: wsTitle || 'Worksheet', question_ids: selectedQIds, class_level: qbFilters.class_level, total_marks: selectedQIds.length }),
    });
    toast.success('Worksheet created!'); setSelectedQIds([]); setWsTitle(''); fetchWorksheets();
  };

  const shareWorksheet = async (wsId, classId) => {
    await fetch(`${API}/teacher/worksheet/${wsId}/share`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ class_id: classId }) });
    toast.success('Shared with class!');
  };

  const viewWs = async (wsId) => {
    const res = await fetch(`${API}/teacher/worksheet/${wsId}`, { headers: getHeaders() });
    const d = await res.json();
    setViewWorksheet(d.worksheet); setViewWsQuestions(d.questions || []);
  };

  const viewAssignmentResults = async (assignmentId) => {
    const res = await fetch(`${API}/teacher/assignment/${assignmentId}/results`, { headers: getHeaders() });
    setViewResults(await res.json());
  };

  const saveProfile = async () => {
    await fetch(`${API}/teacher/profile`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(profileForm) });
    toast.success('Profile updated!'); setEditProfile(false); fetchProfile();
  };

  const changePassword = async () => {
    const res = await fetch(`${API}/teacher/change-password`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(passwordForm) });
    if (!res.ok) { toast.error('Incorrect current password'); return; }
    toast.success('Password changed!'); setPasswordForm({ old_password: '', new_password: '' });
  };

  const downloadWorksheetPDF = async () => {
    if (!viewWorksheet || !viewWsQuestions.length) return;
    toast.info('Generating PDF...');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pw = 190;
    let y = 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(viewWorksheet.title || 'Worksheet', 105, y, { align: 'center' });
    y += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Class ${viewWorksheet.class_level || ''} | Total: ${viewWsQuestions.length} questions | Marks: ${viewWorksheet.total_marks || viewWsQuestions.length}`, 105, y, { align: 'center' });
    y += 10;
    doc.setDrawColor(200);
    doc.line(10, y, 200, y);
    y += 8;
    viewWsQuestions.forEach((q, i) => {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      const qLabel = `Q${i + 1}. [${q.question_type || 'mcq'}] [${q.marks || 1}m]`;
      doc.text(qLabel, 10, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const text = q.question_text || q.question_latex || '';
      const lines = doc.splitTextToSize(text, pw);
      doc.text(lines, 14, y);
      y += lines.length * 5 + 2;
      if (q.option_a) {
        ['A', 'B', 'C', 'D'].forEach(opt => {
          const val = q[`option_${opt.toLowerCase()}`];
          if (val) { doc.text(`   (${opt}) ${val}`, 14, y); y += 5; }
        });
      }
      y += 4;
    });
    doc.save(`${viewWorksheet.title || 'worksheet'}.pdf`);
    toast.success('PDF downloaded!');
  };

  const chapters = config?.config_chapters?.filter(c => c.class_key === String(qbFilters.class_level)) || [];

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: BookOpen },
    { key: 'classes', label: 'Classes', icon: Users },
    { key: 'questions', label: 'Q-Bank', icon: Search },
    { key: 'worksheets', label: 'Sheets', icon: FileText },
    { key: 'grading', label: 'AI Grade', icon: Sparkles },
    { key: 'profile', label: 'Profile', icon: User },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}><div className="space-y-3 w-full max-w-md px-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-[#F0E6D8] rounded-2xl animate-pulse" />)}</div></div>;

  return (
    <div className="min-h-screen pb-6" style={{ background: '#FFF8F0' }}>
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">Teacher Portal</h1>
        <span className="ml-auto text-sm font-bold text-slate-500">{user?.name}</span>
        <button data-testid="teacher-logout-btn" onClick={handleLogout} className="p-1.5 hover:bg-red-50 rounded-lg"><LogOut className="w-4 h-4 text-red-400" /></button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-3 py-2 bg-white border-b border-[#E5DACE] overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} data-testid={`teacher-tab-${t.key}`} onClick={() => {
            setActiveTab(t.key);
            if (t.key === 'profile') fetchProfile();
            if (t.key === 'questions') fetchQuestionBank();
            if (t.key === 'worksheets') fetchWorksheets();
          }}
            className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all
              ${activeTab === t.key ? 'bg-[#1B4F8A] text-white' : 'text-slate-500 hover:bg-[#F0E6D8]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-4">
        {/* ─── Dashboard Tab ─── */}
        {activeTab === 'dashboard' && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[{ l:'Classes', v: dashboard?.total_classes||0, c:'#1B4F8A' },{ l:'Students', v: dashboard?.total_students||0, c:'#FF6B35' },{ l:'Assignments', v: dashboard?.total_assignments||0, c:'#06D6A0' }].map(s => (
                <Card key={s.l} className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-3 text-center">
                  <p className="text-2xl font-extrabold" style={{ color: s.c }}>{s.v}</p>
                  <p className="text-xs font-bold text-slate-500">{s.l}</p>
                </CardContent></Card>
              ))}
            </div>
            {dashboard?.recent_assignments?.length > 0 && (
              <>
                <h3 className="font-bold text-[#1B4F8A] text-sm mb-2">Recent Assignments</h3>
                {dashboard.recent_assignments.map(a => (
                  <Card key={a.id} className="border-2 border-[#E5DACE] rounded-2xl mb-2 cursor-pointer" onClick={() => { viewAssignmentResults(a.id); setActiveTab('results'); }}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <ClipboardList className="w-5 h-5 text-[#FFD23F]" />
                      <div className="flex-1"><p className="font-bold text-[#1B4F8A] text-sm">{a.title}</p><p className="text-xs text-slate-400">{a.total_questions}q</p></div>
                      <Eye className="w-4 h-4 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </>
        )}

        {/* ─── Classes Tab ─── */}
        {activeTab === 'classes' && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-[#1B4F8A]">My Classes</h2>
              <Button data-testid="create-class-btn" onClick={() => setShowCreateClass(true)} className="bg-[#FF6B35] text-white font-bold rounded-full px-4 py-2 text-xs"><Plus className="w-3 h-3 mr-1" /> New</Button>
            </div>
            {showCreateClass && (
              <Card className="mb-4 border-2 border-[#FF6B35] rounded-2xl"><CardContent className="p-4 space-y-2">
                <Input data-testid="class-name-input" placeholder="Class Name" value={newClass.name} onChange={e => setNewClass({...newClass, name: e.target.value})} className="rounded-xl border-2 border-[#E5DACE]" />
                <div className="flex gap-2">
                  <select value={newClass.class_level} onChange={e => setNewClass({...newClass, class_level: parseInt(e.target.value)})} className="flex-1 rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold">
                    {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
                  </select>
                  <Input placeholder="Section" value={newClass.section} onChange={e => setNewClass({...newClass, section: e.target.value})} className="flex-1 rounded-xl border-2 border-[#E5DACE]" />
                </div>
                <div className="flex gap-2">
                  <Button data-testid="save-class-btn" onClick={createClass} className="bg-[#06D6A0] text-white font-bold rounded-full text-sm px-4">Create</Button>
                  <Button onClick={() => setShowCreateClass(false)} variant="ghost" className="text-sm">Cancel</Button>
                </div>
              </CardContent></Card>
            )}
            {(dashboard?.classes || []).map(cls => (
              <Card key={cls.id} className={`border-2 rounded-2xl mb-2 ${selectedClass?.id === cls.id ? 'border-[#1B4F8A]' : 'border-[#E5DACE]'}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setSelectedClass(cls); fetchStudents(cls.id); }}>
                    <div className="w-10 h-10 rounded-xl bg-[#1B4F8A] flex items-center justify-center text-white font-bold text-sm">{cls.class_level}</div>
                    <div className="flex-1"><p className="font-bold text-[#1B4F8A] text-sm">{cls.name}</p><p className="text-xs text-slate-400">{cls.student_count||0} students</p></div>
                    <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(cls.join_code); toast.success(`Code copied!`); }}>
                      <Copy className="w-4 h-4 text-slate-400" />
                    </button>
                    <span className="text-xs font-mono font-bold text-[#FF6B35]">{cls.join_code}</span>
                  </div>
                  {selectedClass?.id === cls.id && (
                    <div className="mt-3 pt-3 border-t border-[#E5DACE]">
                      <div className="flex gap-2 mb-2">
                        <Input placeholder="Student Code" value={studentCode} onChange={e => setStudentCode(e.target.value)} className="flex-1 rounded-xl border-2 border-[#E5DACE] text-sm" />
                        <Button onClick={addStudent} className="bg-[#1B4F8A] text-white font-bold rounded-full text-xs px-3"><UserPlus className="w-3 h-3 mr-1" /> Add</Button>
                      </div>
                      {classStudents.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm py-1 px-2 rounded-lg bg-[#F0E6D8]/50 mb-1">
                          <div className="w-6 h-6 rounded-full bg-[#1B4F8A] text-white text-xs font-bold flex items-center justify-center">{(s.student?.name||'?')[0]}</div>
                          <span className="font-bold text-[#1B4F8A] flex-1">{s.student?.name}</span>
                          <span className="text-xs text-slate-400">{s.streak?.current_streak||0}🔥</span>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2">
                        <Button onClick={() => { setNewAssignment({...newAssignment, class_id: cls.id}); setShowCreateAssignment(true); }} className="bg-[#FFD23F] text-[#1B4F8A] font-bold rounded-full text-xs"><ClipboardList className="w-3 h-3 mr-1" /> Assign</Button>
                        <Button onClick={() => deleteClass(cls.id)} variant="ghost" className="text-red-500 text-xs"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {showCreateAssignment && (
              <Card className="mt-4 border-2 border-[#FFD23F] rounded-2xl"><CardContent className="p-4 space-y-2">
                <h3 className="font-bold text-[#1B4F8A] text-sm">Create Assignment</h3>
                <Input placeholder="Title" value={newAssignment.title} onChange={e => setNewAssignment({...newAssignment, title: e.target.value})} className="rounded-xl border-2 border-[#E5DACE]" />
                <Input type="date" value={newAssignment.due_date} onChange={e => setNewAssignment({...newAssignment, due_date: e.target.value})} className="rounded-xl border-2 border-[#E5DACE]" />
                {selectedQIds.length > 0 && <p className="text-xs font-bold text-[#06D6A0]">{selectedQIds.length} questions selected from Q-Bank</p>}
                <div className="flex gap-2">
                  <Button onClick={createAssignment} className="bg-[#06D6A0] text-white font-bold rounded-full text-sm px-4">Create</Button>
                  <Button onClick={() => setShowCreateAssignment(false)} variant="ghost" className="text-sm">Cancel</Button>
                </div>
              </CardContent></Card>
            )}
          </>
        )}

        {/* ─── Question Bank Tab ─── */}
        {activeTab === 'questions' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <select value={qbFilters.class_level} onChange={e => { setQbFilters({...qbFilters, class_level: e.target.value}); setQbPage(1); }} className="rounded-xl border-2 border-[#E5DACE] px-2 py-2 text-xs font-bold">
                {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
              </select>
              <select value={qbFilters.chapter} onChange={e => setQbFilters({...qbFilters, chapter: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] px-2 py-2 text-xs font-bold">
                <option value="">All Chapters</option>
                {chapters.map(ch => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
              </select>
              <select value={qbFilters.question_type} onChange={e => setQbFilters({...qbFilters, question_type: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] px-2 py-2 text-xs font-bold">
                <option value="">All Types</option>
                {(config?.config_question_types||[]).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <Button onClick={fetchQuestionBank} className="bg-[#1B4F8A] text-white font-bold rounded-full text-xs"><Search className="w-3 h-3 mr-1" /> Search</Button>
            </div>
            {selectedQIds.length > 0 && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-[#06D6A0]/10 rounded-xl border border-[#06D6A0]">
                <Check className="w-4 h-4 text-[#06D6A0]" />
                <span className="text-xs font-bold text-[#06D6A0]">{selectedQIds.length} selected</span>
                <Input placeholder="Worksheet title" value={wsTitle} onChange={e => setWsTitle(e.target.value)} className="flex-1 rounded-lg border border-[#E5DACE] text-xs h-8" />
                <Button onClick={createWorksheet} className="bg-[#06D6A0] text-white font-bold rounded-full text-xs px-3 h-8">Create Worksheet</Button>
                <Button onClick={() => setSelectedQIds([])} variant="ghost" className="text-xs h-8">Clear</Button>
              </div>
            )}
            <p className="text-xs font-bold text-slate-400 mb-2">{qbTotal} questions found</p>
            <div className="space-y-2">
              {qbQuestions.map(q => (
                <Card key={q.id} className={`border-2 rounded-2xl cursor-pointer transition-all ${selectedQIds.includes(q.id) ? 'border-[#06D6A0] bg-[#06D6A0]/5' : 'border-[#E5DACE]'}`}
                  onClick={() => setSelectedQIds(prev => prev.includes(q.id) ? prev.filter(x => x !== q.id) : [...prev, q.id])}>
                  <CardContent className="p-3 flex items-start gap-2">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${selectedQIds.includes(q.id) ? 'bg-[#06D6A0] border-[#06D6A0]' : 'border-[#E5DACE]'}`}>
                      {selectedQIds.includes(q.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex gap-1 mb-1">
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-[#1B4F8A]/10 text-[#1B4F8A]">C{q.class_level}</span>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-[#FF6B35]/10 text-[#FF6B35]">{q.question_type}</span>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-md bg-[#06D6A0]/10 text-[#06D6A0]">{q.difficulty}</span>
                        <span className="text-xs text-slate-400">{q.marks}m</span>
                      </div>
                      <div className="text-xs font-bold text-[#1B4F8A] line-clamp-2"><MathRenderer text={q.question_latex || q.question_text} /></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {qbTotal > 15 && (
              <div className="flex justify-center gap-2 mt-3">
                <Button disabled={qbPage <= 1} onClick={() => { setQbPage(p => p-1); fetchQuestionBank(); }} variant="ghost" className="text-xs">Prev</Button>
                <span className="text-xs font-bold text-slate-400 py-2">Page {qbPage}</span>
                <Button disabled={qbPage*15 >= qbTotal} onClick={() => { setQbPage(p => p+1); fetchQuestionBank(); }} variant="ghost" className="text-xs">Next</Button>
              </div>
            )}
          </>
        )}

        {/* ─── Worksheets Tab ─── */}
        {activeTab === 'worksheets' && !viewWorksheet && !showCreateSheet && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-[#1B4F8A]">My Worksheets</h2>
              <Button data-testid="create-worksheet-btn" onClick={() => setShowCreateSheet(true)}
                className="bg-[#8338EC] text-white font-bold rounded-full text-xs px-3">
                <Plus className="w-3 h-3 mr-1" /> New Test
              </Button>
            </div>
            {worksheets.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-[#E5DACE] mb-3" />
                <p className="text-sm text-slate-400 mb-3">No worksheets yet</p>
                <Button onClick={() => setShowCreateSheet(true)} className="bg-[#FF6B35] text-white font-bold rounded-full text-xs px-4">Create First Test</Button>
              </div>
            ) : worksheets.map(ws => (
              <Card key={ws.id} className="border-2 border-[#E5DACE] rounded-2xl mb-2">
                <CardContent className="p-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#8338EC]" />
                  <div className="flex-1">
                    <p className="font-bold text-[#1B4F8A] text-sm">{ws.title}</p>
                    <p className="text-xs text-slate-400">{ws.total_questions}q | Class {ws.class_level}</p>
                  </div>
                  <button onClick={() => viewWs(ws.id)} className="p-1.5 hover:bg-[#F0E6D8] rounded-lg"><Eye className="w-4 h-4 text-slate-400" /></button>
                  {dashboard?.classes?.length > 0 && (
                    <button onClick={() => shareWorksheet(ws.id, dashboard.classes[0].id)} className="p-1.5 hover:bg-[#F0E6D8] rounded-lg" title="Share with first class"><Share2 className="w-4 h-4 text-[#06D6A0]" /></button>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        )}
        {activeTab === 'worksheets' && showCreateSheet && !viewWorksheet && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setShowCreateSheet(false)}><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
              <h2 className="text-base font-bold text-[#1B4F8A]">Create Test Paper</h2>
            </div>
            <TestGenerator classLevel={qbFilters.class_level || 6} mode="teacher"
              onSave={async (questions, title, totalMarks) => {
                try {
                  const res = await fetch(`${API}/teacher/worksheet/create`, {
                    method: 'POST', headers: getHeaders(),
                    body: JSON.stringify({ title, class_level: qbFilters.class_level || 6, question_ids: questions.map(q => q.id), total_marks: totalMarks }),
                  });
                  if (res.ok) {
                    toast.success('Worksheet created!');
                    setShowCreateSheet(false);
                    fetchWorksheets();
                  } else {
                    toast.error('Failed to save worksheet');
                  }
                } catch { toast.error('Save failed'); }
              }}
            />
          </>
        )}
        {activeTab === 'worksheets' && viewWorksheet && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setViewWorksheet(null)}><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
              <h2 className="text-base font-bold text-[#1B4F8A] flex-1">{viewWorksheet.title}</h2>
              <Button data-testid="download-worksheet-pdf-btn" onClick={downloadWorksheetPDF} className="bg-[#8338EC] text-white font-bold rounded-full text-xs px-3"><Download className="w-3 h-3 mr-1" /> PDF</Button>
            </div>
            {viewWsQuestions.map((q, i) => (
              <Card key={q.id} className="border-2 border-[#E5DACE] rounded-2xl mb-2">
                <CardContent className="p-3">
                  <p className="text-xs font-bold text-slate-400 mb-1">Q{i+1} [{q.question_type}] [{q.marks}m]</p>
                  <div className="text-sm font-bold text-[#1B4F8A]"><MathRenderer text={q.question_latex || q.question_text} /></div>
                  {q.correct_option && <p className="text-xs text-[#06D6A0] mt-1">Answer: {q.correct_option}</p>}
                  {q.correct_answer && <p className="text-xs text-[#06D6A0] mt-1">Answer: {q.correct_answer}</p>}
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* ─── AI Grading Tab ─── */}
        {activeTab === 'grading' && (
          <>
            <h2 className="text-base font-bold text-[#1B4F8A] mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5" /> AI Batch Grading</h2>
            <p className="text-sm text-slate-500 mb-4">Automatically grade subjective answers using AI. Select a class to grade all ungraded submissions.</p>
            <div className="space-y-3 mb-4">
              {(dashboard?.classes || []).map(c => (
                <Card key={c.id} className="border-2 border-[#E5DACE] rounded-2xl">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-sm text-[#1B4F8A]">{c.name}</p>
                      <p className="text-xs text-slate-500">Class {c.class_level} · {c.student_count || 0} students</p>
                    </div>
                    <Button data-testid={`grade-class-${c.id}`} onClick={async () => {
                      setGrading(true); setGradeResults(null);
                      try {
                        const res = await fetch(`${API}/teacher/grade-batch`, {
                          method: 'POST', headers: getHeaders(),
                          body: JSON.stringify({ class_id: c.id })
                        });
                        const data = await res.json();
                        setGradeResults(data);
                        toast.success(`Graded ${data.graded} answers!`);
                      } catch { toast.error('Grading failed'); }
                      finally { setGrading(false); }
                    }} disabled={grading}
                      className="bg-[#8338EC] text-white font-bold rounded-full text-xs px-3">
                      {grading ? 'Grading...' : 'Grade All'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {(!dashboard?.classes || dashboard.classes.length === 0) && (
                <p className="text-sm text-center text-slate-400">No classes yet. Create a class first!</p>
              )}
            </div>
            {gradeResults && (
              <Card data-testid="grade-results" className="border-2 border-[#06D6A0] rounded-2xl">
                <CardContent className="p-4">
                  <p className="font-bold text-sm text-[#1B4F8A] mb-2">Grading Results</p>
                  <p className="text-sm text-slate-600">{gradeResults.message}</p>
                  {gradeResults.results?.map((r, i) => (
                    <div key={i} className="mt-2 p-2 bg-gray-50 rounded-xl text-xs">
                      <p><b>Answer:</b> {r.answer_id?.slice(0, 8)}...</p>
                      <p><b>Score:</b> {r.grade?.marks_awarded}/{r.grade?.max_marks}</p>
                      <p><b>Feedback:</b> {r.grade?.feedback}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── Profile Tab ─── */}
        {activeTab === 'profile' && (
          <>
            <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
              <CardContent className="p-4 space-y-3">
                <h3 className="font-bold text-[#1B4F8A] text-sm flex items-center gap-1"><User className="w-4 h-4" /> Profile</h3>
                {editProfile ? (
                  <>
                    <Input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} placeholder="Name" className="rounded-xl border-2 border-[#E5DACE]" />
                    <Input value={profileForm.school_name} onChange={e => setProfileForm({...profileForm, school_name: e.target.value})} placeholder="School Name" className="rounded-xl border-2 border-[#E5DACE]" />
                    <div className="flex gap-2">
                      <Button onClick={saveProfile} className="bg-[#06D6A0] text-white font-bold rounded-full text-sm px-4">Save</Button>
                      <Button onClick={() => setEditProfile(false)} variant="ghost" className="text-sm">Cancel</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm"><b>Name:</b> {profile?.user?.name}</p>
                    <p className="text-sm"><b>Phone:</b> {profile?.user?.phone}</p>
                    <p className="text-sm"><b>School:</b> {profile?.profile?.school_name || 'Not set'}</p>
                    <Button onClick={() => setEditProfile(true)} variant="outline" className="text-xs font-bold border-2 border-[#E5DACE] rounded-full">Edit Profile</Button>
                  </>
                )}
              </CardContent>
            </Card>
            <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
              <CardContent className="p-4 space-y-3">
                <h3 className="font-bold text-[#1B4F8A] text-sm flex items-center gap-1"><Lock className="w-4 h-4" /> Change Password</h3>
                <Input type="password" placeholder="Current Password" value={passwordForm.old_password} onChange={e => setPasswordForm({...passwordForm, old_password: e.target.value})} className="rounded-xl border-2 border-[#E5DACE]" />
                <Input type="password" placeholder="New Password" value={passwordForm.new_password} onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})} className="rounded-xl border-2 border-[#E5DACE]" />
                <Button onClick={changePassword} className="bg-[#FF6B35] text-white font-bold rounded-full text-sm px-4">Change Password</Button>
              </CardContent>
            </Card>
            <Button data-testid="teacher-logout-bottom-btn" onClick={handleLogout} className="w-full bg-red-500 text-white font-bold rounded-full py-3">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </>
        )}

        {/* ─── Assignment Results ─── */}
        {activeTab === 'results' && viewResults && (
          <>
            <button onClick={() => setActiveTab('dashboard')} className="flex items-center gap-1 text-sm font-bold text-[#1B4F8A] mb-3"><ArrowLeft className="w-4 h-4" /> Back</button>
            <h2 className="text-base font-bold text-[#1B4F8A] mb-3">{viewResults.assignment?.title} - Results</h2>
            {viewResults.results?.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No submissions yet</p>
            ) : viewResults.results?.map((r, i) => (
              <Card key={i} className="border-2 border-[#E5DACE] rounded-2xl mb-2">
                <CardContent className="p-3 flex items-center justify-between">
                  <div><p className="font-bold text-[#1B4F8A] text-sm">{r.student_name}</p><p className="text-xs text-slate-400">{r.correct_answers}/{r.total_questions} correct</p></div>
                  <span className={`text-lg font-black ${r.score_percent >= 70 ? 'text-[#06D6A0]' : r.score_percent >= 50 ? 'text-[#FFD23F]' : 'text-red-500'}`}>{r.score_percent}%</span>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
